import React, { useState, useEffect, useMemo } from "react";
import { Button, TextField, Box, Typography, Grid, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import Select from "react-select";
import { useGetPortfolioSummaryQuery } from "store/slices/apis/portfolioSummarySlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import moment from "moment";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { useUpdateWarehousingMutation } from "store/slices/apis/warehousingTrackerApiSlice";

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const calenderSVG = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_894_610)">
            <path d="M12.6667 1.33333H12V0.666667C12 0.489856 11.9298 0.320286 11.8047 0.195262C11.6797 0.0702379 11.5101 0 11.3333 0C11.1565 0 10.987 0.0702379 10.8619 0.195262C10.7369 0.320286 10.6667 0.489856 10.6667 0.666667V1.33333H5.33333V0.666667C5.33333 0.489856 5.2631 0.320286 5.13807 0.195262C5.01305 0.0702379 4.84348 0 4.66667 0C4.48986 0 4.32029 0.0702379 4.19526 0.195262C4.07024 0.320286 4 0.489856 4 0.666667V1.33333H3.33333C2.4496 1.33439 1.60237 1.68592 0.97748 2.31081C0.352588 2.93571 0.00105857 3.78294 0 4.66667L0 12.6667C0.00105857 13.5504 0.352588 14.3976 0.97748 15.0225C1.60237 15.6474 2.4496 15.9989 3.33333 16H12.6667C13.5504 15.9989 14.3976 15.6474 15.0225 15.0225C15.6474 14.3976 15.9989 13.5504 16 12.6667V4.66667C15.9989 3.78294 15.6474 2.93571 15.0225 2.31081C14.3976 1.68592 13.5504 1.33439 12.6667 1.33333ZM1.33333 4.66667C1.33333 4.13623 1.54405 3.62753 1.91912 3.25245C2.29419 2.87738 2.8029 2.66667 3.33333 2.66667H12.6667C13.1971 2.66667 13.7058 2.87738 14.0809 3.25245C14.456 3.62753 14.6667 4.13623 14.6667 4.66667V5.33333H1.33333V4.66667ZM12.6667 14.6667H3.33333C2.8029 14.6667 2.29419 14.456 1.91912 14.0809C1.54405 13.7058 1.33333 13.1971 1.33333 12.6667V6.66667H14.6667V12.6667C14.6667 13.1971 14.456 13.7058 14.0809 14.0809C13.7058 14.456 13.1971 14.6667 12.6667 14.6667Z" fill="#433F5E" />
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#433F5E" />
            <path d="M4.66656 11C5.21885 11 5.66656 10.5523 5.66656 10C5.66656 9.44772 5.21885 9 4.66656 9C4.11428 9 3.66656 9.44772 3.66656 10C3.66656 10.5523 4.11428 11 4.66656 11Z" fill="#433F5E" />
            <path d="M11.3334 11C11.8857 11 12.3334 10.5523 12.3334 10C12.3334 9.44772 11.8857 9 11.3334 9C10.7811 9 10.3334 9.44772 10.3334 10C10.3334 10.5523 10.7811 11 11.3334 11Z" fill="#433F5E" />
        </g>
        <defs>
            <clipPath id="clip0_894_610">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);


const EditWarehousing = (props) => {
    const { open, handleClose, rowData, refetchCounts } = props;
    const { projectId: routeProjectId } = useParams();
    const dispatch = useDispatch();
    const [projectType, setProjectType] = useState(localStorage.getItem("projectType") || "all");
    const module = "Warehousing"

    const { data: tenantNameData } = useGetMasterDataQuery({ module: module, category: 'tenantName', ProjectId: routeProjectId });
    const { data: escalationData } = useGetMasterDataQuery({ module: module, category: 'Escalation', ProjectId: routeProjectId });
    const { data: statusData } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: routeProjectId });

    const tenantOptions =
        tenantNameData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const statusOptions =
        statusData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const escalationOptions =
        escalationData?.data
            ?.map((item) => ({
                label: item?.value,
                value: item?.value
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];

    const { data, error, isLoading, isFetching: isProjectFething, refetch } = useGetAllProjectsQuery(projectType);
    const { data: portfolioData, isFetching: portfolioLoader, isError: portfolioError } = useGetPortfolioSummaryQuery();

    const projectData = data?.data || [];
    const filteredProjectData = projectData.filter((data) => data.active == true);

    const projectOptions = (filteredProjectData || []).map(p => ({
        value: p.projectId,
        label: p.projectName,
        projectType: p.projectType,
    }));

    const portfolioOptions = (portfolioData?.data || []).map(p => ({
        value: p.portfolioName,
        label: p.portfolioName,
        projectType: p.portFolioType,
    }));

    const portfolioSelectOptions = useMemo(
        () => [
            // { projectType: "all", label: "All Portfolio" },
            ...portfolioOptions,
        ],
        [portfolioOptions]
    );

    const handlePortfolioChange = (option, setFieldValue) => {
        let projectType = option?.projectType || "all";
        setProjectType(projectType);
        localStorage.setItem("projectType", projectType);
        setFieldValue("product", option?.value || "");
    };

    const [updateWarehousing] = useUpdateWarehousingMutation();

    const initialFormData = {
        product: "",
        project: "",
        warehouseName: "",
        status: "",
        tenantName: "",
        uwPlinthAreaSqFt: "",
        uwLCD: "",
        uwRCD: "",
        uwLeaseTenureMonths: "",
        uwRentFreeMonths: "",
        uwRentalPerSqFt: "",
        uwCAMPerSqFt: "",
        uwEscalation: "",
        actEscalation: "",
        actPlinthAreaSqFt: "",
        actLCD: "",
        actRCD: "",
        actLeaseTenureMonths: "",
        actRentFreeMonths: "",
        actTIRentalPerSqFt: "",
        actNetEffectiveRentalPerSqFt: "",
        actCAMPerSqFt: "",
        actLockInMonths: "",
        adjustedUnderwritingRental: "",
        netEffectiveRental: "",
        difference: "",
        projectId: ""
    };

    const [initialValues, setInitialValues] = useState(initialFormData);

    useEffect(() => {
        if (rowData && open) {
            setInitialValues({
                product: rowData?.product || "",
                project: rowData?.project || "",
                warehouseName: rowData?.warehouseName || "",
                status: rowData?.status || "",
                tenantName: rowData?.tenantName || "",
                uwPlinthAreaSqFt: rowData?.uwPlinthAreaSqFt || "",
                uwLCD: rowData?.uwLCD
                    ? moment.utc(rowData.uwLCD).format("YYYY-MM-DD")
                    : null,
                uwRCD: rowData?.uwRCD
                    ? moment.utc(rowData.uwRCD).format("YYYY-MM-DD")
                    : null,
                uwLeaseTenureMonths: rowData?.uwLeaseTenureMonths || "",
                uwRentFreeMonths: rowData?.uwRentFreeMonths || "",
                uwRentalPerSqFt: rowData?.uwRentalPerSqFt || "",
                uwCAMPerSqFt: rowData?.uwCAMPerSqFt || "",
                uwEscalation: rowData?.uwEscalation || "",
                actEscalation: rowData?.actEscalation || "",
                actPlinthAreaSqFt: rowData?.actPlinthAreaSqFt || "",
                actLCD: rowData?.actLCD
                    ? moment.utc(rowData.actLCD).format("YYYY-MM-DD")
                    : null,
                actRCD: rowData?.actRCD
                    ? moment.utc(rowData.actRCD).format("YYYY-MM-DD")
                    : null,
                actLeaseTenureMonths: rowData?.actLeaseTenureMonths || "",
                actRentFreeMonths: rowData?.actRentFreeMonths || "",
                actTIRentalPerSqFt: rowData?.actTIRentalPerSqFt || "",
                actNetEffectiveRentalPerSqFt: rowData?.actNetEffectiveRentalPerSqFt || "",
                actCAMPerSqFt: rowData?.actCAMPerSqFt || "",
                actLockInMonths: rowData?.actLockInMonths || "",
                adjustedUnderwritingRental: rowData?.adjustedUnderwritingRental || "",
                netEffectiveRental: rowData?.netEffectiveRental || "",
                difference: rowData?.difference || "",
                projectId: rowData?.projectID || "",
            });
        }
    }, [rowData, open]);

    const validationSchema = Yup.object({
        warehouseName: Yup.string().trim().required("Warehouse Name is required"),
        // tenantName: Yup.string().required("Tenant Name is required"),
        uwPlinthAreaSqFt: Yup.number().typeError("Must be a number").positive("Must be positive"),
        // uwLCD: Yup.string(),
        // uwRCD: Yup.string(),
        uwLeaseTenureMonths: Yup.number().typeError("Must be a number").positive("Must be positive"),
        uwRentFreeMonths: Yup.number().typeError("Must be a number").min(0),
        uwRentalPerSqFt: Yup.number().typeError("Must be a number").min(0),
        uwCAMPerSqFt: Yup.number().typeError("Must be a number").min(0),
        // uwEscalation: Yup.number().typeError("Must be a number").min(0),
        actPlinthAreaSqFt: Yup.number().typeError("Must be a number").positive("Must be positive"),
        // actLCD: Yup.string(),
        // actRCD: Yup.string(),
        actLeaseTenureMonths: Yup.number().typeError("Must be a number").positive("Must be positive"),
        actRentFreeMonths: Yup.number().typeError("Must be a number").min(0),
        actTIRentalPerSqFt: Yup.number().typeError("Must be a number").min(0),
        actNetEffectiveRentalPerSqFt: Yup.number().typeError("Must be a number").min(0),
        actCAMPerSqFt: Yup.number().typeError("Must be a number").min(0),
        actLockInMonths: Yup.number().typeError("Must be a number").min(0),
        adjustedUnderwritingRental: Yup.number().typeError("Must be a number"),
        netEffectiveRental: Yup.number().typeError("Must be a number"),
        difference: Yup.number().typeError("Must be a number"),
        product: Yup.string().required("Product is required"),
        project: Yup.string().required("Project is required"),
    });

    const stringToDecimal = (value) => {
        if (value === "" || value === null || value === undefined) return null;

        const num = Number(value);
        if (isNaN(num)) return null;

        return Number(num.toFixed(2));
    };

    const emptyGUID = "00000000-0000-0000-0000-000000000000";

    const handleSubmit = async (values, { setSubmitting }) => {
        const reqBody = {
            warehouseLeaseId: rowData?.warehouseLeaseId,
            projectId: values?.projectId || "",
            product: values?.product,
            project: values?.project,
            warehouseName: values?.warehouseName,
            status: values?.status,
            tenantName: values?.tenantName,
            uwEscalation: values?.uwEscalation,
            actEscalation: values?.actEscalation,

            uwLCD: values?.uwLCD
                ? moment.utc(values?.uwLCD, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            uwRCD: values?.uwRCD
                ? moment.utc(values?.uwRCD, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            actLCD: values?.actLCD
                ? moment.utc(values?.actLCD, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            actRCD: values?.actRCD
                ? moment.utc(values?.actRCD, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,

            uwPlinthAreaSqFt: values?.uwPlinthAreaSqFt ? stringToDecimal(values?.uwPlinthAreaSqFt) : 0,
            uwLeaseTenureMonths: values?.uwLeaseTenureMonths ? stringToDecimal(values?.uwLeaseTenureMonths) : 0,
            uwRentFreeMonths: values?.uwRentFreeMonths ? stringToDecimal(values?.uwRentFreeMonths) : 0,
            uwRentalPerSqFt: values?.uwRentalPerSqFt ? stringToDecimal(values?.uwRentalPerSqFt) : 0,
            uwCAMPerSqFt: values?.uwCAMPerSqFt ? stringToDecimal(values?.uwCAMPerSqFt) : 0,

            actPlinthAreaSqFt: values?.actPlinthAreaSqFt ? stringToDecimal(values?.actPlinthAreaSqFt) : 0,
            actLeaseTenureMonths: values?.actLeaseTenureMonths ? stringToDecimal(values?.actLeaseTenureMonths) : 0,
            actRentFreeMonths: values?.actRentFreeMonths ? stringToDecimal(values?.actRentFreeMonths) : 0,
            actTIRentalPerSqFt: values?.actTIRentalPerSqFt ? stringToDecimal(values?.actTIRentalPerSqFt) : 0,
            actNetEffectiveRentalPerSqFt: values?.actNetEffectiveRentalPerSqFt ? stringToDecimal(values?.actNetEffectiveRentalPerSqFt) : 0,
            actCAMPerSqFt: values?.actCAMPerSqFt ? stringToDecimal(values?.actCAMPerSqFt) : 0,
            actLockInMonths: values?.actLockInMonths ? stringToDecimal(values?.actLockInMonths) : 0,

            adjustedUnderwritingRental: values?.adjustedUnderwritingRental ? stringToDecimal(values?.adjustedUnderwritingRental) : 0,
            netEffectiveRental: values?.netEffectiveRental ? stringToDecimal(values?.netEffectiveRental) : 0,
            difference: values?.difference ? stringToDecimal(values?.difference) : 0,
        };

        // console.log("Update Warehousing Data:", reqBody);

        try {
            const response = await updateWarehousing({ body: reqBody }).unwrap();

            if (response) {
                openNotification(response.message || "Updated successfully", "success");
                if (refetchCounts) refetchCounts();
                handleClose();
            } else {
                openNotification(response?.message || "Something went wrong", "error");
            }
        } catch (error) {
            console.error("Update Error:", error);
            let errorMessage = "Something went wrong";
            if (error?.data?.message) errorMessage = error.data.message;
            else if (error?.error) errorMessage = error.error;
            else if (error?.message) errorMessage = error.message;
            openNotification(errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: "alert",
                alert: { color: type },
                close: false,
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <>
            <Dialog
                open={open}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: "5px",
                        borderTopRightRadius: "5px",
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                        overflowX: "hidden",
                    },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#ECECEC",
                        pb: 2,
                        pt: 3,
                        width: "100%",
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "27px",
                                lineHeight: "100%",
                                color: "#140F36",
                                fontFamily: "Outfit, sans-serif",
                            }}
                        >
                            Edit Warehousing
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Formik
                        enableReinitialize={true}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldTouched,
                            setFieldValue,
                            submitCount
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    {routeProjectId == undefined &&
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={labelTextStyle}>
                                                Product
                                            </Typography>

                                            <Select
                                                name="product"
                                                options={portfolioSelectOptions}
                                                value={
                                                    portfolioSelectOptions.find(
                                                        (opt) => opt.value === values.product
                                                    ) || null
                                                }
                                                onChange={(option) => handlePortfolioChange(option, setFieldValue)}
                                                onBlur={() => setFieldTouched("product", true)}
                                                placeholder="Select Portfolio"
                                            />

                                            {(touched.product || submitCount > 0) && errors.product && (
                                                <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.product}
                                                </Typography>
                                            )}
                                        </Grid>
                                    }

                                    {routeProjectId == undefined &&
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Typography sx={labelTextStyle}>
                                                Project
                                            </Typography>

                                            <Select
                                                name="project"
                                                options={[{ value: emptyGUID, label: "All Projects" }, ...projectOptions]}
                                                value={
                                                    [{ value: emptyGUID, label: "All Projects" }, ...projectOptions]
                                                        .find(opt => opt.value === values.projectId) || null
                                                }
                                                onChange={(option) => {
                                                    setFieldValue("projectId", option?.value);
                                                    setFieldValue("project", option?.label);
                                                }}
                                                onBlur={() => setFieldTouched("project", true)}
                                                placeholder="Select Project"
                                            />

                                            {(touched.project || submitCount > 0) && errors.project && (
                                                <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.project}
                                                </Typography>
                                            )}
                                        </Grid>
                                    }
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Warehouse Name</Typography>
                                        <TextField
                                            fullWidth
                                            name="warehouseName"
                                            value={values.warehouseName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Warehouse Name"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />

                                        {touched.warehouseName && errors.warehouseName && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.warehouseName}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Status</Typography>
                                        <Select
                                            name="status"
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === values.status) || null}
                                            onChange={(option) => setFieldValue("status", option.value)}
                                            onBlur={() => setFieldTouched("status", true)}
                                            placeholder="Select Status"
                                        />
                                        {touched.status && errors.status && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.status}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Tenant Name</Typography>
                                        <Select
                                            name="tenantName"
                                            options={tenantOptions}
                                            value={tenantOptions.find(opt => opt.value === values.tenantName) || null}
                                            onChange={(option) => setFieldValue("tenantName", option.value)}
                                            onBlur={() => setFieldTouched("tenantName", true)}
                                            placeholder="Select Tenant Name"
                                        />
                                        {touched.tenantName && errors.tenantName && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.tenantName}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Typography variant="h6" sx={{ mt: 2, mb: 1, width: "100%" }}>Underwriting</Typography>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Plinth Area Sqft</Typography>
                                        <TextField
                                            fullWidth
                                            name="uwPlinthAreaSqFt"
                                            value={values.uwPlinthAreaSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Plinth Area"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.uwPlinthAreaSqFt && errors.uwPlinthAreaSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwPlinthAreaSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>LCD</Typography>
                                        <TextField
                                            type="date"
                                            name="uwLCD"
                                            value={values.uwLCD}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                // Trigger the native date picker
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                    >
                                                        {calenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>RCD</Typography>
                                        <TextField
                                            type="date"
                                            name="uwRCD"
                                            value={values.uwRCD}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                // Trigger the native date picker
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                    >
                                                        {calenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Lease Tenure (Months)</Typography>
                                        <TextField
                                            fullWidth
                                            name="uwLeaseTenureMonths"
                                            value={values.uwLeaseTenureMonths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Months"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}

                                        />
                                        {touched.uwLeaseTenureMonths && errors.uwLeaseTenureMonths && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwLeaseTenureMonths}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Rent Free (Months)</Typography>
                                        <TextField
                                            fullWidth
                                            name="uwRentFreeMonths"
                                            value={values.uwRentFreeMonths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Months"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.uwRentFreeMonths && errors.uwRentFreeMonths && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwRentFreeMonths}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Rental INR Psf PM On Plinth Excl CAM Excl TI</Typography>
                                        <TextField
                                            fullWidth
                                            name="uwRentalPerSqFt"
                                            value={values.uwRentalPerSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.uwRentalPerSqFt && errors.uwRentalPerSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwRentalPerSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>CAM INR Psf PM On Plinth</Typography>
                                        <TextField
                                            fullWidth
                                            name="uwCAMPerSqFt"
                                            value={values.uwCAMPerSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.uwCAMPerSqFt && errors.uwCAMPerSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwCAMPerSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Escalation (%)</Typography>
                                        <Select
                                            name="uwEscalation"
                                            options={escalationOptions}
                                            value={escalationOptions.find(opt => opt.value === values.uwEscalation) || null}
                                            onChange={(option) => setFieldValue("uwEscalation", option.value)}
                                            onBlur={() => setFieldTouched("uwEscalation", true)}
                                            placeholder="Select Escalation"
                                        />
                                        {touched.uwEscalation && errors.uwEscalation && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.uwEscalation}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Typography variant="h6" sx={{ mt: 2, mb: 1, width: "100%" }}>Actuals</Typography>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Plinth Area Sqft (Actual)</Typography>
                                        <TextField
                                            fullWidth
                                            name="actPlinthAreaSqFt"
                                            value={values.actPlinthAreaSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Plinth Area Actual"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actPlinthAreaSqFt && errors.actPlinthAreaSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actPlinthAreaSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>LCD (Actual)</Typography>
                                        <TextField
                                            type="date"
                                            name="actLCD"
                                            value={values.actLCD}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                // Trigger the native date picker
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                    >
                                                        {calenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>RCD (Actual)</Typography>
                                        <TextField
                                            type="date"
                                            name="actRCD"
                                            value={values.actRCD}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                // Trigger the native date picker
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                    >
                                                        {calenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Lock-In (Months)</Typography>
                                        <TextField
                                            fullWidth
                                            name="actLockInMonths"
                                            value={values.actLockInMonths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter months"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actLockInMonths && errors.actLockInMonths && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actLockInMonths}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>TI Rental INR Psf PM</Typography>
                                        <TextField
                                            fullWidth
                                            name="actTIRentalPerSqFt"
                                            value={values.actTIRentalPerSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actTIRentalPerSqFt && errors.actTIRentalPerSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actTIRentalPerSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Net Effective Rental INR Psf PM On Plinth Excl CAM Excl TI</Typography>
                                        <TextField
                                            fullWidth
                                            name="actNetEffectiveRentalPerSqFt"
                                            value={values.actNetEffectiveRentalPerSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actNetEffectiveRentalPerSqFt && errors.actNetEffectiveRentalPerSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actNetEffectiveRentalPerSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Lease Tenure Months (Actual)</Typography>
                                        <TextField
                                            fullWidth
                                            name="actLeaseTenureMonths"
                                            value={values.actLeaseTenureMonths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Months"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actLeaseTenureMonths && errors.actLeaseTenureMonths && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actLeaseTenureMonths}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Rent Free Months (Actual)</Typography>
                                        <TextField
                                            fullWidth
                                            name="actRentFreeMonths"
                                            value={values.actRentFreeMonths}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Months"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actRentFreeMonths && errors.actRentFreeMonths && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actRentFreeMonths}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>CAM INR Psf PM On Plinth (Actual)</Typography>
                                        <TextField
                                            fullWidth
                                            name="actCAMPerSqFt"
                                            value={values.actCAMPerSqFt}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.actCAMPerSqFt && errors.actCAMPerSqFt && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actCAMPerSqFt}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Escalation Actual (%)</Typography>
                                        <Select
                                            name="actEscalation"
                                            options={escalationOptions}
                                            value={escalationOptions.find(opt => opt.value === values.actEscalation) || null}
                                            onChange={(option) => setFieldValue("actEscalation", option.value)}
                                            onBlur={() => setFieldTouched("actEscalation", true)}
                                            placeholder="Select Escalation"
                                        />
                                        {touched.actEscalation && errors.actEscalation && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.actEscalation}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Typography variant="h6" sx={{ mt: 2, mb: 1, width: "100%" }}>Rental Comparison</Typography>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Adjusted Underwriting Rental</Typography>
                                        <TextField
                                            fullWidth
                                            name="adjustedUnderwritingRental"
                                            value={values.adjustedUnderwritingRental}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.adjustedUnderwritingRental && errors.adjustedUnderwritingRental && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.adjustedUnderwritingRental}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Net Effective Rental INR Psf PM On Plinth Excl CAM Excl TI (Rental)</Typography>
                                        <TextField
                                            fullWidth
                                            name="netEffectiveRental"
                                            value={values.netEffectiveRental}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Amount"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.netEffectiveRental && errors.netEffectiveRental && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.netEffectiveRental}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Difference</Typography>
                                        <TextField
                                            fullWidth
                                            name="difference"
                                            value={values.difference}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Difference"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#5F5F5F",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        "&:-webkit-autofill": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:hover": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                        "&:-webkit-autofill:focus": {
                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                            WebkitTextFillColor: "#0A0A0C",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.difference && errors.difference && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.difference}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Button
                                                onClick={handleClose}
                                                sx={{
                                                    textTransform: "none",
                                                    color: "#F15744",
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    borderRadius: "25px",
                                                    fontWeight: 500,
                                                    px: "20px",
                                                    py: "15px",
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={isSubmitting}
                                                sx={{
                                                    backgroundColor: "#00344C",
                                                    borderRadius: "25px",
                                                    textTransform: "none",
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    color: "#FFFFFF",
                                                    px: "20px",
                                                    py: "15px",
                                                }}
                                            >
                                                {isSubmitting ? "Updating..." : "Update"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditWarehousing;